import React, { useState, useEffect } from "react";
import Loader from "../../Shared/Loader";
import "../../../css/EditLogTable.css";
import api from "../../../api/axiosConfig";
import EditLogDetailsModal from "./EditLogDetailsModal";

export interface LogData {
  id: string;
  editActivityId: string;
  appointmentClusterId: string;
  centerId: string;
  centerName: string;
  centerType: string;
  city: string;
  editedOn: string;
  cancelledBookingsCount: number;
  newAppointmentsCreatedCount: number;
}

interface LogEditItem {
  edit_activity_id: string;
  edited_on: string;
  appointment_cluster_id: string;
  center_id: string;
  cancelled_bookings_count: number;
  new_appointments_created_count: number;
  [key: string]: any;
}

interface LogEditsResponse {
  page: number;
  limit: number;
  total: number;
  items: LogEditItem[];
}

interface CenterDetails {
  name: string;
  centerType: string;
  city: string;
}

const formatDateDisplay = (dateStr?: string): string => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const EditLogTable: React.FC = () => {
  const [logs, setLogs] = useState<LogData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchLogData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("accessToken");
        if (!token) throw new Error("Authentication token not found.");

        const res = await api.appointment_api.get<LogEditsResponse>(
          "/api/cluster/logs/edits?page=1&limit=50",
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const items = res.data.items || [];
        if (items.length === 0) {
          setLogs([]);
          setLoading(false);
          return;
        }

        const uniqueCenterIds = Array.from(new Set(items.map((item) => item.center_id)));

        const centerDetailPromises = uniqueCenterIds.map((id) =>
          api.center_api.get<{ success: boolean; data: CenterDetails }>(`/api/v1/centers/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );

        const centerResponses = await Promise.all(centerDetailPromises);

        const centerDataMap = new Map<string, CenterDetails>();
        centerResponses.forEach((cRes, i) => {
          if (cRes.data?.success && cRes.data.data) {
            centerDataMap.set(uniqueCenterIds[i], cRes.data.data);
          }
        });

        const finalLogs: LogData[] = items.map((item, idx) => {
          const center = centerDataMap.get(item.center_id);
          return {
            id: item.edit_activity_id || `log-${idx}`,
            editActivityId: item.edit_activity_id || `edt-${idx + 1}`,
            appointmentClusterId: item.appointment_cluster_id,
            centerId: item.center_id,
            centerName: center?.name || "Unknown Center",
            centerType: center?.centerType || "Unknown",
            city: center?.city || "Unknown",
            editedOn: item.edited_on,
            cancelledBookingsCount: item.cancelled_bookings_count || 0,
            newAppointmentsCreatedCount: item.new_appointments_created_count || 0,
          };
        });

        setLogs(finalLogs);
      } catch (err: any) {
        console.error("Failed to fetch log data:", err);
        setError(err.message || "Could not load edit logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogData();
  }, []);

  useEffect(() => {
    if (logs.length === 0) {
      setCurrentPage(1);
      return;
    }
    const totalPages = Math.ceil(logs.length / itemsPerPage);
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [logs, currentPage]);

  const totalPages = Math.ceil(logs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentLogs = logs.slice(startIndex, startIndex + itemsPerPage);

  const handleRowClick = (log: LogData) => {
    setSelectedLog(log);
    setIsLogModalOpen(true);
  };

  const handleCloseLogModal = () => {
    setIsLogModalOpen(false);
    setSelectedLog(null);
  };

  if (loading) {
    return <Loader text="Loading Edited Appointments..." />;
  }

  if (error) {
    return <div style={{ color: "red", padding: "20px", textAlign: "center" }}>{error}</div>;
  }

  return (
    <div className="edit-log-container">
      <h3 className="edit-table-title">List of Edited Clusters</h3>
      <table className="edit-log-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Edited On</th>
            <th>Center Name</th>
            <th>Center Category</th>
            <th>City</th>
            <th>No. of Cancelled Booking ID’s</th>
            <th>No. of New Appt IDs created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentLogs.length > 0 ? (
            currentLogs.map((row, index) => (
              <tr key={row.id}>
                <td>{startIndex + index + 1}</td>
                <td>{formatDateDisplay(row.editedOn)}</td>
                <td>{row.centerName}</td>
                <td>
                  <span className="tag">{row.centerType}</span>
                </td>
                <td>{row.city}</td>
                <td>{row.cancelledBookingsCount}</td>
                <td>{row.newAppointmentsCreatedCount}</td>
                <td>
                  <button
                    className="view-edit-btn"
                    onClick={() => handleRowClick(row)}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#0d6efd",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                    }}
                  >
                    View Logs
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", padding: "16px" }}>
                No edited appointments found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="pagination edit-log-pagination">
          <button
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={currentPage === pageNum ? "active" : ""}
            >
              {pageNum}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {isLogModalOpen && selectedLog && (
        <EditLogDetailsModal logData={selectedLog} onClose={handleCloseLogModal} />
      )}
    </div>
  );
};

export default EditLogTable;